/**
 * Local notes (SPEC.md §4.3, §7) — prose that isn't a task and doesn't need a home.
 *
 * A task's `area_id` is NOT NULL because filing a task is deciding where it goes (§0). A
 * note is the opposite case on purpose: an idea worth keeping that is not, itself, a
 * decision waiting to be made. Both `area_id` and `project_id` stay optional here for that
 * reason, unlike everywhere else in the app.
 *
 * The only way in used to be resolving an inbox item as a note. The `+` in the header adds
 * one directly, folded shut by default so the card doesn't compete with capture's one true
 * front door — the title bar at the very top of the page is still the fastest way in for
 * anything short enough to type there. `promoted_path` is reserved for a future "push to
 * the vault" action; nothing here writes to Nextcloud yet.
 */

import { useState } from "react";
import type { Area, Note, Project } from "../api";
import { AiReasoningDisclosure } from "./AiReasoningDisclosure";
import { ClampedText } from "./ClampedText";
import { Pencil, Plus, Trash } from "./icons";
import { NoteForm } from "./NoteForm";
import {
  EMPTY_NOTE_FIELDS,
  destinationToIds,
  noteAiTitle,
  noteToFieldValues,
  type NoteFieldValues,
} from "./noteFieldValues";
import { useSettings } from "../settings";
import type { AiRefined } from "../aiRefine";
import type { CaptureTier } from "../api";

/** An edited note's fields as the API takes them: both ids always present, so moving a note out of
 * an area or project clears it. */
export type NoteEditValues = {
  title: string;
  body: string | null;
  area_id: string | null;
  project_id: string | null;
};

type CreateValues = { title: string; body: string | null; area_id?: string; project_id?: string };

export function NotesSection({
  notes,
  areas,
  projects,
  onCreate,
  onCreateWithAi,
  onEdit,
  onEditWithAi,
  onDelete,
}: {
  notes: Note[];
  areas: Area[];
  projects: Project[];
  onCreate: (values: CreateValues) => Promise<void>;
  /** The add form's AI bulb. Omitted, the form has no bulb. */
  onCreateWithAi?: (values: CreateValues) => Promise<void>;
  onEdit: (id: string, patch: Record<string, unknown>) => Promise<void>;
  /** The edit form's AI bulb: updates the note where it stands. Omitted, the form has no bulb. */
  onEditWithAi?: (
    id: string,
    values: NoteEditValues,
    tier: CaptureTier | null,
  ) => Promise<AiRefined<Note>>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);

  return (
    <section className="panel">
      <header>
        <h2>Notes{notes.length > 0 && ` · ${notes.length}`}</h2>
        <span className="row">
          <span className="muted small">Kept, not filed anywhere in particular</span>
          <button
            type="button"
            className="btn ghost small"
            aria-label="Add a note"
            title="Add a note"
            onClick={() => setAdding((v) => !v)}
          >
            <Plus size={14} />
          </button>
        </span>
      </header>

      {adding && (
        <AddNoteForm
          areas={areas}
          projects={projects}
          onCreate={onCreate}
          onCreateWithAi={onCreateWithAi}
          onCancel={() => setAdding(false)}
        />
      )}

      {notes.length === 0 && !adding && (
        <p className="muted small">
          Nothing kept as a note yet. Add one with the + above, or resolve an inbox item as
          one.
        </p>
      )}

      {notes.length > 0 && (
        <ul className="note-list">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              areas={areas}
              projects={projects}
              onEdit={onEdit}
              onEditWithAi={onEditWithAi}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function AddNoteForm({
  areas,
  projects,
  onCreate,
  onCreateWithAi,
  onCancel,
}: {
  areas: Area[];
  projects: Project[];
  onCreate: (values: CreateValues) => Promise<void>;
  onCreateWithAi?: (values: CreateValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<NoteFieldValues>({ ...EMPTY_NOTE_FIELDS });
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sendToAi() {
    if (!onCreateWithAi) return;
    setBusy(true);
    setAsking(true);
    setError(null);
    try {
      await onCreateWithAi({
        title: noteAiTitle(values),
        body: values.body || null,
        ...destinationToIds(values.destination),
      });
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setAsking(false);
    }
  }

  async function submit() {
    if (!values.title.trim()) return;
    setBusy(true);
    try {
      await onCreate({
        title: values.title.trim(),
        body: values.body || null,
        ...destinationToIds(values.destination),
      });
      onCancel();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="note editing" style={{ marginBottom: "0.6rem" }}>
      <NoteForm
        values={values}
        onChange={(patch) => setValues((current) => ({ ...current, ...patch }))}
        areas={areas}
        projects={projects}
        busy={busy}
        submitLabel="Save note"
        onSubmit={submit}
        onCancel={onCancel}
        onAi={onCreateWithAi ? () => void sendToAi() : undefined}
        busyLabel={asking ? "Asking the AI…" : undefined}
        error={error}
      />
    </div>
  );
}

export function NoteRow({
  note,
  areas,
  projects,
  onEdit,
  onEditWithAi,
  onDelete,
}: {
  note: Note;
  areas: Area[];
  projects: Project[];
  onEdit: (id: string, patch: Record<string, unknown>) => Promise<void>;
  onEditWithAi?: (
    id: string,
    values: NoteEditValues,
    tier: CaptureTier | null,
  ) => Promise<AiRefined<Note>>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [values, setValues] = useState<NoteFieldValues>(() => noteToFieldValues(note));
  // Changed since the AI last answered: an answer still showing gives the buttons back.
  const [touched, setTouched] = useState(false);
  const settings = useSettings();

  // A project implies its area, so showing both would just repeat the filing decision the
  // note was written specifically to skip — the one tag names whichever is the closer fit.
  const tag = note.project_id
    ? projects.find((p) => p.id === note.project_id)?.name
    : note.area_id
      ? areas.find((a) => a.id === note.area_id)?.name
      : undefined;

  async function save() {
    if (!values.title.trim()) return;
    setBusy(true);
    try {
      // `area_id`/`project_id` default to null so switching from one to the other — or to
      // neither — clears the one not named, rather than leaving a stale id sitting under a
      // freshly-set one.
      await onEdit(note.id, {
        title: values.title.trim(),
        body: values.body || null,
        area_id: null,
        project_id: null,
        ...destinationToIds(values.destination),
      });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (
      !window.confirm(
        `Delete the note "${note.title}"?\n\n` +
          "This cannot be undone - unlike dismissing an inbox item, there is no record kept.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await onDelete(note.id);
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <li className="note editing">
        <NoteForm
          values={values}
          onChange={(patch) => {
            setTouched(true);
            setValues((current) => ({ ...current, ...patch }));
          }}
          areas={areas}
          projects={projects}
          busy={busy}
          onSubmit={save}
          onCancel={() => setEditing(false)}
          editAi={
            onEditWithAi
              ? {
                  onRetry: async (tier) => {
                    setTouched(false);
                    const answer = await onEditWithAi(
                      note.id,
                      {
                        title: values.title.trim(),
                        body: values.body || null,
                        area_id: null,
                        project_id: null,
                        ...destinationToIds(values.destination),
                      },
                      tier,
                    );
                    setValues(noteToFieldValues(answer.item));
                    return answer;
                  },
                  // Clearing a held answer closes the form; a failure cleared leaves it open.
                  onSettled: (result) => {
                    if (result) setEditing(false);
                  },
                  keepOpen: touched,
                }
              : undefined
          }
        />
      </li>
    );
  }

  return (
    <li className="note">
      <div className="note-head">
        <strong className="note-title">{note.title}</strong>
        <span className="note-actions">
          {tag && <span className="badge note-tag">{tag}</span>}
          {/*
            Its own nested group, never broken apart - the two buttons move together as one
            unit onto the actions' own line rather than being free to separate from each
            other the way two ungrouped flex children could.
          */}
          <span className="note-buttons">
            <button
              className="btn ghost small"
              onClick={() => setEditing(true)}
              aria-label={`Edit: ${note.title}`}
              title="Edit"
            >
              <Pencil />
            </button>
            <button
              className="btn ghost small danger"
              onClick={() => void remove()}
              disabled={busy}
              aria-label={`Delete: ${note.title}`}
              title="Delete"
            >
              <Trash />
            </button>
          </span>
        </span>
      </div>
      {/*
        A row of its own, full width, below the title/badge row rather than sharing a
        column with it - a long project badge (or a long title) otherwise squeezes this
        into whatever sliver is left over, which is the opposite of "fills the width".
      */}
      {note.body && (
        <ClampedText
          text={note.body}
          maxLines={settings.noteBodyLines}
          textClassName="muted small note-body"
        />
      )}
      <AiReasoningDisclosure reasoning={note.ai_reasoning} />
    </li>
  );
}
